import { Star, ExternalLink, PenSquare } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Link ufficiale profilo Google Business — Davide Perugini Supercar & Classic Cars
// Link diretto alla scrittura di una recensione sul profilo Google
export const GOOGLE_REVIEWS_URL = "https://g.page/r/CXK7p0zAexvREAE/review";

function GoogleGlyph({ size = 22 }) {
  // Logo Google multicolore (SVG inline, senza dipendenze esterne)
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

/**
 * variant:
 *  - "full"     → CTA grande "Leggi le recensioni" + link piccolo "Lascia recensione"
 *  - "compact"  → richiamo a 5 stelle per la homepage vicino ai contatti
 */
export default function GoogleReviewsCTA({ variant = "full" }) {
  const { lang } = useLang();

  if (variant === "compact") {
    return (
      <a
        href={GOOGLE_REVIEWS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="group block border border-white/10 hover:border-white/40 bg-[#0a0a0a] p-6 sm:p-8 transition-colors"
        data-testid="google-reviews-callout"
      >
        <div className="flex items-center gap-5 flex-wrap">
          <GoogleGlyph size={30} />
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 mb-1.5" aria-label="5 su 5 stelle">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={16} className="fill-[#F4B400] text-[#F4B400]" strokeWidth={1.2} />
              ))}
              <span className="ml-1 text-white text-sm font-medium tabular-nums">5.0</span>
            </div>
            <div className="font-serif text-xl sm:text-2xl text-white leading-tight">
              {lang === "en" ? "Read our reviews on " : "Leggi le nostre recensioni su "}
              <span className="italic text-neutral-400">Google</span>
            </div>
          </div>
          <ExternalLink size={18} className="text-neutral-500 group-hover:text-white transition-colors" />
        </div>
      </a>
    );
  }

  // variant "full"
  return (
    <div className="mb-14" data-testid="google-reviews-cta">
      <a
        href={GOOGLE_REVIEWS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center justify-between gap-6 border border-white/15 hover:border-white/60 bg-[#0a0a0a] p-6 sm:p-8 transition-all hover:bg-white/[0.03]"
        data-testid="google-reviews-read-btn"
      >
        <div className="flex items-center gap-5 flex-wrap">
          <div className="p-3 bg-white rounded-full flex items-center justify-center shrink-0">
            <GoogleGlyph size={26} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2" aria-label="5 su 5 stelle">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={18} className="fill-[#F4B400] text-[#F4B400]" strokeWidth={1.2} />
              ))}
              <span className="ml-2 label-eyebrow text-neutral-500">Google Business</span>
            </div>
            <div className="font-serif text-2xl sm:text-3xl text-white leading-tight">
              {lang === "en" ? "Read our verified reviews" : "Leggi le nostre recensioni verificate"}
            </div>
          </div>
        </div>
        <ExternalLink size={22} className="text-neutral-500 group-hover:text-white group-hover:translate-x-1 transition-all shrink-0" />
      </a>

      <div className="mt-4 flex justify-end">
        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 label-eyebrow text-neutral-400 hover:text-white border border-white/10 hover:border-white/40 px-4 py-2.5 transition-colors"
          data-testid="google-reviews-write-btn"
        >
          <PenSquare size={13} />
          {lang === "en" ? "Leave a review on Google" : "Lascia una recensione su Google"}
        </a>
      </div>
    </div>
  );
}
